import { authFetch } from "@/lib/authFetch";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  UserPlus,
  FolderOpen,
  Users,
  Landmark,
  Target,
  Calendar,
  Search,
  X,
  LogOut,
  Settings,
  Loader2,
  User,
  Sun,
  Moon,
  UserCircle,
  Key,
  GraduationCap,
  Inbox,

} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";

const CFI_LOGO = "/images/cfi-logo-main.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Credit Firm Inc Locations
const CFI_LOCATIONS = [
  "Amarillo, TX",
  "Lubbock, TX",
  "Midland, TX",
  "Odessa, TX",
  "Abilene, TX",
  "San Angelo, TX",
  "Austin, TX",
  "Waco, TX",
  "Tyler, TX",
  "Roswell, NM",
  "Hobbs, NM",
  "Las Cruces, NM",
  "Charlotte, NC",
  "Online Only",
  "Other",
];

/**
 * Reusable Sidebar Component with Typeahead Search
 */
const Sidebar = ({ isOpen, onClose, isAdmin = true, isClient = false }) => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Debounced search function
  const searchClients = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await authFetch(`${API}/clients?search=${encodeURIComponent(query)}&limit=10`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.clients || []);
        setShowResults(true);
      }
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Handle input change with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce the search
    debounceRef.current = setTimeout(() => {
      searchClients(value);
    }, 300);
  };

  // Handle client selection
  const handleSelectClient = (client) => {
    const bp = isClient ? "/client" : isAdmin ? "/admin" : "/team";
    const clientId = client.client_id || client.id;
    if (clientId) {
      navigate(`${bp}/clients/${clientId}`);
      setSearchQuery("");
      setSearchResults([]);
      setShowResults(false);
      onClose?.();
    }
  };

  // Handle form submit (fallback to All Clients page)
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const bp = isClient ? "/client" : isAdmin ? "/admin" : "/team";
      navigate(`${bp}/clients?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
      setSearchResults([]);
      setShowResults(false);
      onClose?.();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const basePath = isClient ? "/client" : isAdmin ? "/admin" : "/team";

  const clientNavItems = [
    { path: "/client/dashboard", label: "My Dashboard", icon: LayoutDashboard },
    { path: "/client/resources", label: "Credit Firm Resources", icon: Target },
    { path: "/client/billing", label: "Update My Payment Method", icon: Key },
  ];

  const navItems = isClient
    ? clientNavItems
    : isAdmin
    ? [
        { path: `${basePath}/dashboard`, label: "Dashboard", icon: LayoutDashboard },
        { path: `${basePath}/add-client`, label: "Add Client", icon: UserPlus },
        { path: `${basePath}/clients`, label: "All Clients", icon: FolderOpen },
        // Hide global admin tools for scoped BH location admins
        ...(user?.role === "BH_LOCATION_ADMIN" ? [] : [
          { path: `${basePath}/users`, label: "User Management", icon: Users },
          ...(user?.role === "CREDITFIRMINC_ADMIN" ? [{ path: `${basePath}/affiliates`, label: "Affiliate Management", icon: Landmark }] : []),
          { path: `${basePath}/access-requests`, label: "Access Requests", icon: Inbox },
          { path: `${basePath}/api-keys`, label: "API Keys", icon: Key },
        ]),
        { path: `${basePath}/resources`, label: "Credit Firm Resources", icon: Target },
        { path: `${basePath}/training`, label: "Portal Training", icon: GraduationCap },
      ]
    : [
        { path: `${basePath}/dashboard`, label: "Dashboard", icon: LayoutDashboard },
        { path: `${basePath}/add-client`, label: "Add Client", icon: UserPlus },
        { path: `${basePath}/clients`, label: "My Clients", icon: FolderOpen },
        { path: `${basePath}/profile`, label: "My Partner Profile", icon: UserCircle },
        { path: `${basePath}/resources`, label: "Credit Firm Resources", icon: Target },
        { path: `${basePath}/training`, label: "Portal Training", icon: GraduationCap },
      ];

  // Display "Referral Partner" instead of legacy role names
  const getRoleLabel = () => {
    if (isClient) {
      return "Client Portal";
    }
    if (!isAdmin) {
      return "Referral Partner";
    }
    const role = user?.role;
    if (role === "CREDITFIRMINC_ADMIN") return "CFI Admin";
    if (role === "CREDIT_COUNSELOR") return "Credit Counselor";
    if (role === "BH_ADMIN") return "BH Admin";
    if (role === "BH_LOCATION_ADMIN") {
      return user?.betenbough_location
        ? `BH ${user.betenbough_location} Admin`
        : "BH Location Admin";
    }
    if (role === "BETENBOUGH_ADMIN") return "Admin";
    return role?.replace(/_/g, " ") || "User";
  };

  const roleLabel = getRoleLabel();

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-[#0d5a1a] via-[#0a3d12] to-[#050505] text-white transform transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full overflow-y-auto">
          {/* Logo and Search */}
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <img
                  src={CFI_LOGO}
                  alt="Credit Firm Inc"
                  className="h-20 w-auto"
                />
              </div>
              <button
                onClick={onClose}
                className="md:hidden text-white/60 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* Search Input with Typeahead - hide for client users */}
            {!isClient && (
            <div ref={searchRef} className="relative">
              <form onSubmit={handleSearch}>
                <div className="relative">
                  {isSearching ? (
                    <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 animate-spin" />
                  ) : (
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  )}
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={() => searchQuery.length >= 2 && setShowResults(true)}
                    placeholder="Search clients..."
                    className="w-full pl-9 pr-3 py-2 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/40 focus:outline-none focus:border-[#84c77f] focus:ring-1 focus:ring-[#84c77f]"
                    data-testid="sidebar-search-input"
                  />
                </div>
              </form>

              {/* Search Results Dropdown */}
              {showResults && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 max-h-64 overflow-y-auto z-50">
                  {searchResults.map((client) => (
                    <button
                      key={client.client_id || client.id}
                      onClick={() => handleSelectClient(client)}
                      className="w-full px-3 py-2 flex items-center gap-3 hover:bg-slate-100 text-left transition-colors border-b border-slate-100 last:border-0"
                      data-testid={`search-result-${client.client_id || client.id}`}
                    >
                      <div className="w-8 h-8 bg-[#357c2a] rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="h-4 w-4 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {client.first_name} {client.last_name}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {client.email || client.phone || "No contact info"}
                        </p>
                      </div>
                    </button>
                  ))}
                  {/* View All Results option */}
                  <button
                    onClick={handleSearch}
                    className="w-full px-3 py-2 text-center text-sm text-[#357c2a] hover:bg-slate-100 font-medium"
                  >
                    View all results →
                  </button>
                </div>
              )}

              {/* No results message */}
              {showResults && searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 p-3 z-50">
                  <p className="text-sm text-slate-500 text-center">No clients found</p>
                </div>
              )}
            </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === item.path
                    ? "bg-[#22c55e] text-white font-semibold shadow-lg shadow-green-500/30"
                    : "text-white/70 hover:bg-white/10 hover:text-white"
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <item.icon className="h-5 w-5" />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Book with Counselor Button - hide for client users */}
          {!isClient && (
          <div className="p-4 border-t border-white/10">
            <Link
              to={`${basePath}/booking`}
              className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                location.pathname.includes("/booking")
                  ? "bg-[#22c55e] text-white shadow-lg shadow-green-500/30"
                  : "bg-[#22c55e] hover:bg-[#16a34a] text-white shadow-lg shadow-green-500/20"
              }`}
            >
              <Calendar className="h-5 w-5" />
              <span className="text-sm font-medium">Book with Counselor</span>
            </Link>
          </div>
          )}

          {/* Theme Toggle */}
          <div className="px-4 py-2">
            <button
              onClick={toggleTheme}
              className="flex items-center justify-between w-full px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
              data-testid="theme-toggle"
            >
              <span className="text-sm text-white/70">
                {isDarkMode ? "Dark Mode" : "Light Mode"}
              </span>
              <div className="flex items-center gap-2">
                {isDarkMode ? (
                  <Moon className="h-4 w-4 text-white/70" />
                ) : (
                  <Sun className="h-4 w-4 text-yellow-400" />
                )}
                <div className={`w-10 h-5 rounded-full transition-colors ${isDarkMode ? 'bg-slate-600' : 'bg-[#22c55e]'} relative`}>
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${isDarkMode ? 'left-5' : 'left-0.5'}`} />
                </div>
              </div>
            </button>
          </div>

          {/* User Profile Dropdown */}
          <div className="p-4 border-t border-white/10">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-white/10 transition-colors">
                  {user?.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium">
                        {user?.name?.charAt(0) || "U"}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-white/50 truncate">{roleLabel}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem asChild>
                  <Link
                    to={`${basePath}/settings`}
                    className="flex items-center cursor-pointer"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Account Settings
                  </Link>
                </DropdownMenuItem>
                {!isAdmin && (
                  <DropdownMenuItem asChild>
                    <Link
                      to={`${basePath}/profile`}
                      className="flex items-center cursor-pointer"
                    >
                      <UserCircle className="h-4 w-4 mr-2" />
                      My Partner Profile
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer text-red-600"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
